import { injectAskAIBridge } from './askAI.js';
import { logClientError } from './errorLog.js';

const assetCache = new Map();
const frameLoads = new WeakMap();

async function fetchAsset(url, kind) {
  if (!assetCache.has(url)) {
    const pending = (async () => {
      let response = await fetch(url, { cache: 'reload' });
      if (response.status === 403) {
        // Preserve the signed path while bypassing a cached denial once.
        const retryUrl = new URL(url);
        retryUrl.searchParams.set('_ll_retry', Date.now().toString());
        response = await fetch(retryUrl.href, { cache: 'no-store' });
      }
      const type = response.headers.get('content-type') || '';
      if (!response.ok || !(kind === 'script' ? /javascript/ : /text\/css/).test(type)) {
        throw new Error(`Lesson library ${new URL(url).pathname}: HTTP ${response.status}, ${type}`);
      }
      return response.text();
    })();
    assetCache.set(url, pending);
    pending.catch(() => assetCache.delete(url));
  }
  return assetCache.get(url);
}

export async function prepareReadOnlyHtml(html) {
  const doc = new DOMParser().parseFromString(String(html || ''), 'text/html');
  const assets = [...doc.querySelectorAll('script[src],link[rel="stylesheet"][href]')];
  await Promise.all(assets.map(async (element) => {
    const kind = element.tagName === 'SCRIPT' ? 'script' : 'style';
    const url = new URL(element.getAttribute(kind === 'script' ? 'src' : 'href'), location.href);
    // Only inline signed platform dependencies, never arbitrary external code.
    if (url.origin !== location.origin || !/^\/kits\/[\w-]+\/[\d.]+\.(js|css)$/.test(url.pathname) || !url.searchParams.has('sig')) return;
    const source = await fetchAsset(url.href, kind);
    const replacement = doc.createElement(kind);
    for (const attribute of element.attributes) {
      if (!['src', 'href', 'rel', 'integrity', 'crossorigin', 'async', 'defer'].includes(attribute.name)) {
        replacement.setAttribute(attribute.name, attribute.value);
      }
    }
    // Inline scripts execute synchronously in document order, before the lesson.
    replacement.textContent = source.replace(kind === 'script' ? /<\/script/gi : /<\/style/gi, kind === 'script' ? '<\\/script' : '<\\/style');
    element.replaceWith(replacement);
  }));
  return '<!doctype html>\n' + doc.documentElement.outerHTML;
}

export async function loadReadOnlyFrame(frame, html, context) {
  const load = {};
  frameLoads.set(frame, load);
  try {
    const prepared = await prepareReadOnlyHtml(html);
    if (!frame.isConnected || frameLoads.get(frame) !== load) return;
    frame.srcdoc = injectAskAIBridge(prepared, { enhanceCanvas: false });
  } catch (error) {
    if (!frame.isConnected || frameLoads.get(frame) !== load) return;
    console.error('Could not load lesson libraries:', error);
    frame.srcdoc = '<!doctype html><p role="alert">Could not load lesson libraries. Reopen this topic to retry.</p>';
    // Best-effort — this is the only place a failure here ever surfaces
    // anywhere but the reporting user's own devtools console (see the
    // fetchAsset/prepareReadOnlyHtml comments above for why it can fail:
    // a 403/404 from a bad/un-archived kit signature, a network drop, a
    // CORS/ad-blocker block, etc). Without this, tracking down a report of
    // this message means combing Cloud Run access logs for the right kit
    // URL with no idea which topic or user it came from.
    logClientError({
      context: 'readOnlyFrame',
      error,
      displayMessage: 'Could not load lesson libraries. Reopen this topic to retry.',
      courseId: context?.courseId,
      topicId: context?.topicId,
    });
  }
}
